import { useDispatch } from "react-redux"
import { Creators } from "model/apiWrapper"
import { useAccountAlias } from "resources/account"
import { get, toUpper } from "lodash"
import { combineTanStackStatuses, DataStatus, OK } from "helpers/data-statuses"
import useCombinedQueries from "./combine-queries"
import { QueryObserverBaseResult } from "@tanstack/react-query"

type PositionSetType = "BENCHMARK" | "PORTFOLIO" | "INDEX" | "RESEARCH_TOPIC" | "ETF" | "BASKET"

interface PositionSet {
  id: string
  type: PositionSetType
}

interface useIsConstituentsDownloadAllowedParams {
  modelId: string
  positionSets: PositionSet[]
}

export default function useIsConstituentsDownloadAllowed({ modelId, positionSets }: useIsConstituentsDownloadAllowedParams): [DataStatus, boolean] {
  const dispatch = useDispatch()
  const accountAlias = useAccountAlias()

  const queries = positionSets
    .filter(({ type }) => toUpper(type) === "INDEX")
    .map(({ id }) => ({
      queryKey: ["position-sets", "constituents-download-allowed", modelId, id],
      queryFn: () => new Promise((resolve, reject) => {
        dispatch(Creators.get(`/api/${accountAlias}/m/${modelId}/indices/${id}/details`, resolve, reject))
      }),
      select: (data: unknown) => {
        return get(data, "allowConstituentsDownload", false)
      },
    }))

  const combine = (queryResults: QueryObserverBaseResult<boolean>[]) => {
    const status = combineTanStackStatuses(queryResults)
    if (status !== OK) {
      return [status, false]
    }

    return [status, queryResults.length === 0 || queryResults.every(({ data }) => data)]
  }

  return useCombinedQueries(
    queries,
    combine,
  )
}
